<script lang="ts">
import { page } from '$app/state'
import {
  STUDY_CHART_JS_GATE,
  type StudyChartJsGate,
} from '$lib/components/study/study-chart-js-gate'
import { baseChartOptions, getStudyChartThemeColors } from '$lib/utils/report/chart-config'
import { getContext } from 'svelte'

interface Props {
  labels: string[]
  data: number[]
  colors: string[]
  /** 'pie' renders a full circle; 'doughnut' uses the cutout prop */
  type?: 'doughnut' | 'pie'
  cutout?: string
  legendPosition?: 'right' | 'bottom' | 'left' | 'top'
  tooltipFormat?: (value: number, label: string, total: number) => string
  active?: boolean
  height?: string
}

let {
  labels,
  data,
  colors,
  type = 'doughnut',
  cutout = '60%',
  legendPosition = 'right',
  tooltipFormat = (v, label) => ` ${label}: ${v}`,
  active = false,
  height = '190px',
}: Props = $props()

let canvas: HTMLCanvasElement
let built = $state(false)
let chartInstance: { destroy(): void } | null = null

const chartJsGate = getContext<StudyChartJsGate | undefined>(STUDY_CHART_JS_GATE)

$effect(() => {
  page.url.search
  void chartJsGate?.ready
  if (!active || !canvas) return
  const Chart = (window as Window & { Chart?: unknown }).Chart as
    | { new (el: HTMLCanvasElement, cfg: object): { destroy(): void } }
    | undefined
  if (!Chart) return

  if (chartInstance) {
    chartInstance.destroy()
    chartInstance = null
  }
  built = true

  const total = data.reduce((s, v) => s + v, 0)

  const host = (canvas.closest('.study-presentation') ?? canvas) as HTMLElement
  const th = getStudyChartThemeColors(host)

  chartInstance = new Chart(canvas, {
    type,
    data: {
      labels,
      datasets: [
        {
          data,
          backgroundColor: colors,
          borderWidth: 0,
          hoverOffset: 6,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      ...(type === 'doughnut' ? { cutout } : {}),
      plugins: {
        legend: {
          position: legendPosition,
          labels: { color: th.legend, boxWidth: 9, font: { size: 9 }, padding: 7 },
        },
        tooltip: {
          ...baseChartOptions.plugins.tooltip,
          backgroundColor: th.tooltipBg,
          borderColor: th.tooltipBorder,
          titleColor: th.tooltipTitle,
          bodyColor: th.tooltipBody,
          callbacks: {
            label: (c: { raw: number; label: string }) => tooltipFormat(c.raw, c.label, total),
          },
        },
      },
    },
  })

  return () => {
    if (chartInstance) {
      chartInstance.destroy()
      chartInstance = null
    }
    built = false
  }
})
</script>

<div class="ch" style="height:{height}">
  <canvas bind:this={canvas}></canvas>
</div>
